import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Configure axios instance with full backend URL
const api = axios.create({
  baseURL: 'https://apigm.sriganapathymedical.shop/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

const UserTypeManager = () => {
  const navigate = useNavigate();
  // State for the form input value
  const [userTypeInput, setUserTypeInput] = useState('');
  // State for the list of user types
  const [userTypes, setUserTypes] = useState([]);
  // State to track which item is being edited
  const [editingId, setEditingId] = useState(null);
  // State for the edit input value
  const [editValue, setEditValue] = useState('');
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch all user types on component mount
  useEffect(() => {
    fetchUserTypes();
  }, []);

  // Fetch user types from API
  const fetchUserTypes = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/user-types');
      setUserTypes(response.data);
    } catch (err) {
      setError('Error fetching user types: ' + (err.response?.data?.error || err.message));
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission to add a new user type
  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedValue = userTypeInput.trim();

    if (trimmedValue === '') {
      alert('Please enter a user type');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/user-types', { name: trimmedValue });
      const newUserType = response.data;
      setUserTypes([...userTypes, newUserType]);

      localStorage.setItem("userType", trimmedValue);

      setUserTypeInput('');
      alert(`User Type "${trimmedValue}" added. Role is now active for configuration.`);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
      console.error('Add error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Save the edited user type
  const saveEdit = async (id) => {
    const trimmedValue = editValue.trim();

    if (trimmedValue === '') {
      alert('User type cannot be empty');
      return;
    }

    setLoading(true);
    try {
      const response = await api.put(`/user-types/${id}`, { name: trimmedValue });
      const updatedUserType = response.data;
      setUserTypes(userTypes.map(type =>
        type.id === id ? updatedUserType : type
      ));
      setEditingId(null);
      setEditValue('');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
      console.error('Update error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Start editing a user type
  const startEditing = (userType) => {
    setEditingId(userType.id);
    setEditValue(userType.name);
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  // Delete a user type
  const deleteUserType = async (id) => {
    if (window.confirm('Are you sure you want to delete this user type?')) {
      setLoading(true);
      try {
        await api.delete(`/user-types/${id}`);
        setUserTypes(userTypes.filter(type => type.id !== id));
      } catch (err) {
        alert('Error: ' + (err.response?.data?.error || err.message));
        console.error('Delete error:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  // Dark theme styles with responsive adjustments
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#0a0e27',
      padding: isMobile ? '20px 10px' : '40px 20px',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif'
    },
    card: {
      maxWidth: '900px',
      margin: '0 auto',
      backgroundColor: '#1a1f3e',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      padding: isMobile ? '20px' : '30px',
      border: '1px solid #2a2f4a'
    },
    title: {
      fontSize: isMobile ? '22px' : '28px',
      fontWeight: '600',
      color: '#ffffff',
      marginBottom: '30px',
      textAlign: 'center',
      borderBottom: '2px solid #2a2f4a',
      paddingBottom: '15px'
    },
    errorMessage: {
      backgroundColor: 'rgba(220, 53, 69, 0.2)',
      color: '#ff6b6b',
      padding: '12px',
      borderRadius: '6px',
      marginBottom: '20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      border: '1px solid #dc3545',
      fontSize: isMobile ? '13px' : '14px'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: '#ff6b6b',
      fontSize: '20px',
      cursor: 'pointer',
      padding: '0 5px'
    },
    form: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '10px' : '15px',
      alignItems: isMobile ? 'stretch' : 'flex-end',
      marginBottom: isMobile ? '30px' : '40px',
      flexWrap: 'wrap'
    },
    formGroup: {
      flex: 1,
      minWidth: isMobile ? '100%' : '250px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '500',
      color: '#a0a5c0'
    },
    input: {
      width: '100%',
      padding: isMobile ? '10px 12px' : '10px 12px',
      fontSize: isMobile ? '14px' : '16px',
      backgroundColor: '#0a0e27',
      border: '1px solid #2a2f4a',
      borderRadius: '6px',
      outline: 'none',
      transition: 'border-color 0.2s',
      boxSizing: 'border-box',
      color: '#ffffff'
    },
    addButton: {
      padding: isMobile ? '10px 20px' : '10px 20px',
      backgroundColor: '#4c9aff',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      height: isMobile ? 'auto' : '42px',
      width: isMobile ? '100%' : 'auto'
    },
    tableContainer: {
      marginTop: '20px',
      overflowX: 'auto'
    },
    subtitle: {
      fontSize: isMobile ? '18px' : '20px',
      fontWeight: '500',
      color: '#ffffff',
      marginBottom: '20px'
    },
    loadingMessage: {
      textAlign: 'center',
      color: '#a0a5c0',
      padding: '40px',
      backgroundColor: '#0a0e27',
      borderRadius: '8px',
      fontSize: isMobile ? '14px' : '16px'
    },
    emptyMessage: {
      textAlign: 'center',
      color: '#a0a5c0',
      padding: '40px',
      backgroundColor: '#0a0e27',
      borderRadius: '8px',
      fontSize: isMobile ? '14px' : '16px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      backgroundColor: '#0a0e27',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
      minWidth: isMobile ? '500px' : '100%'
    },
    tableHeaderRow: {
      backgroundColor: '#0f132e',
      borderBottom: '2px solid #2a2f4a'
    },
    tableHeader: {
      padding: isMobile ? '10px 12px' : '12px 16px',
      textAlign: 'left',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
      color: '#a0a5c0',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    tableRow: {
      borderBottom: '1px solid #2a2f4a',
      transition: 'background-color 0.2s'
    },
    tableCell: {
      padding: isMobile ? '10px 12px' : '12px 16px',
      fontSize: isMobile ? '13px' : '15px',
      color: '#e0e5f0'
    },
    userTypeName: {
      fontWeight: '500',
      color: '#ffffff'
    },
    editInput: {
      padding: '6px 10px',
      fontSize: isMobile ? '13px' : '14px',
      backgroundColor: '#0a0e27',
      border: '1px solid #4c9aff',
      borderRadius: '4px',
      outline: 'none',
      width: isMobile ? '100%' : '100%',
      maxWidth: isMobile ? '150px' : '200px',
      color: '#ffffff',
      boxSizing: 'border-box'
    },
    actionButtons: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '4px' : '8px',
      flexWrap: 'wrap'
    },
    actionButton: {
      padding: isMobile ? '5px 10px' : '6px 12px',
      fontSize: isMobile ? '11px' : '13px',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'opacity 0.2s',
      width: isMobile ? '100%' : 'auto',
      textAlign: 'center'
    },
    editButton: {
      backgroundColor: '#4c9aff',
      color: 'white'
    },
    deleteButton: {
      backgroundColor: '#ff6b6b',
      color: 'white'
    },
    saveButton: {
      backgroundColor: '#51cf66',
      color: 'white'
    },
    cancelButton: {
      backgroundColor: '#6c757d',
      color: 'white'
    },
    manageButton: {
      backgroundColor: '#3b82f6',
      color: 'white'
    }
  };

  // Add hover effects
  const styleSheet = document.createElement("style");
  styleSheet.textContent = `
    button:hover:not(:disabled) {
      opacity: 0.85;
      transform: translateY(-1px);
    }
    button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    input:focus {
      border-color: #4c9aff;
      box-shadow: 0 0 0 2px rgba(76, 154, 255, 0.2);
    }
    tr:hover {
      background-color: #0f132e;
    }
    @media (max-width: 768px) {
      .table-cell {
        font-size: 13px;
        padding: 10px 12px;
      }
      .table-header {
        font-size: 12px;
        padding: 10px 12px;
      }
    }
  `;
  document.head.appendChild(styleSheet);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>User Type Manager</h1>

        {error && (
          <div style={styles.errorMessage}>
            {error}
            <button onClick={() => setError('')} style={styles.closeButton}>×</button>
          </div>
        )}

        {/* Form Section */}
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label htmlFor="userType" style={styles.label}>
              User Type:
            </label>
            <input
              type="text"
              id="userType"
              value={userTypeInput}
              onChange={(e) => setUserTypeInput(e.target.value)}
              placeholder={isMobile ? "Enter user type..." : "Enter user type (e.g., Manager, Guest)"}
              style={styles.input}
              disabled={loading}
            />
          </div>
          <button
            type="submit"
            style={styles.addButton}
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add User Type'}
          </button>
        </form>

        {/* Table Section */}
        <div style={styles.tableContainer}>
          <h2 style={styles.subtitle}>User Types List</h2>
          {loading && userTypes.length === 0 ? (
            <p style={styles.loadingMessage}>Loading...</p>
          ) : userTypes.length === 0 ? (
            <p style={styles.emptyMessage}>No user types added yet. Add one above!</p>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeaderRow}>
                  <th style={styles.tableHeader}>#</th>
                  <th style={styles.tableHeader}>User Type</th>
                  <th style={styles.tableHeader}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {userTypes.map((userType, index) => (
                  <tr key={userType.id} style={styles.tableRow}>
                    <td style={styles.tableCell}>{index + 1}</td>
                    <td style={styles.tableCell}>
                      {editingId === userType.id ? (
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          style={styles.editInput}
                          autoFocus
                          disabled={loading}
                        />
                      ) : (
                        <span style={styles.userTypeName}>{userType.name}</span>
                      )}
                    </td>
                    <td style={styles.tableCell}>
                      {editingId === userType.id ? (
                        <div style={styles.actionButtons}>
                          <button
                            onClick={() => saveEdit(userType.id)}
                            style={{ ...styles.actionButton, ...styles.saveButton }}
                            disabled={loading}
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            style={{ ...styles.actionButton, ...styles.cancelButton }}
                            disabled={loading}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={styles.actionButtons}>
                          <button
                            onClick={() => {
                              localStorage.setItem("userType", userType.name);
                              navigate('/userSettings');
                            }}
                            style={{ ...styles.actionButton, ...styles.manageButton }}
                          >
                            Manage
                          </button>
                          <button
                            onClick={() => startEditing(userType)}
                            style={{ ...styles.actionButton, ...styles.editButton }}
                            disabled={loading}
                          >
                            Update
                          </button>
                          <button
                            onClick={() => deleteUserType(userType.id)}
                            style={{ ...styles.actionButton, ...styles.deleteButton }}
                            disabled={loading}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserTypeManager;